import { Clock } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

const TIMES = Array.from({ length: 24 * 4 }, (_, i) => {
  const h = Math.floor(i / 4)
    .toString()
    .padStart(2, "0");
  const m = ((i % 4) * 15).toString().padStart(2, "0");
  return `${h}:${m}`;
});

export interface TimePickerProps {
  value?: string;
  onValueChange: (time: string) => void;
  className?: string;
}

export function TimePicker({ value, onValueChange, className }: TimePickerProps) {
  const { t } = useTranslation();
  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          variant="default"
          className={cn("h-8 w-[7rem] gap-2 font-mono tabular-nums", className)}
        >
          <Clock size={14} />
          {value ?? "--:--"}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="max-h-72 w-32 overflow-y-auto p-0">
        <ul role="listbox" aria-label={t("common.time-picker.list.aria-label")} className="grid">
          {TIMES.map((time) => (
            <li key={time}>
              <button
                type="button"
                role="option"
                aria-selected={value === time}
                className={cn(
                  "block w-full px-2 py-1 text-left font-mono text-[12px] tabular-nums text-ink-700",
                  "hover:bg-surface-1",
                  value === time && "bg-p-50 text-p-700",
                )}
                onClick={() => onValueChange(time)}
              >
                {time}
              </button>
            </li>
          ))}
        </ul>
      </PopoverContent>
    </Popover>
  );
}
